/**
 * Cases-list columns for the five PA Status output fields (BV-2092 phase 3).
 *
 * BV-2092 put these five leaves in the Cases SEARCH dropdown but could not give them
 * columns: `extractFieldsFromSchema` mints a column per TOP-LEVEL schema field only, and
 * on `pa_status_medbv` the output schema stops at the `drug[]` / `procedure[]` arrays — so
 * no column ever carried these leaves. This module closes that gap without teaching the
 * column extractor about nesting in general (which would flood the Customize dropdown for
 * every array workflow, medbv's `drug[].coverage.copay` and friends included).
 *
 * Two output shapes, one column each, mirroring `searchFieldToDBPaths` in
 * `service/case_records.go`:
 *
 *   - `pa_status_pbm` follows up a single drug and carries the leaves FLAT on
 *     `output_data.data.<leaf>`. Those columns already exist (the leaves are top-level
 *     schema fields), so we ATTACH a descriptor to them rather than adding duplicates.
 *   - `pa_status_medbv` repeats the same leaf names inside every `drug[]` / `procedure[]`
 *     entry, so we APPEND a synthetic column whose cell fans out into both arrays and
 *     renders one `CODE · Value` pill per billable code.
 *
 * Note the asymmetry the server also encodes: the PA leaves hang off `prior_auth_status`,
 * the appeal leaves off `prior_auth_denied` — a denial is what carries an appeal.
 *
 * PURE (no React/IO) so it is unit-testable under the 100% `utils/` coverage rule.
 */
import { get } from 'lodash-es';

import { WorkflowType } from '@/constants/dispositions';
import type { FieldGroup, FormField } from '@/types/formSchema';
import type { AvailableField, EntityLeafCell, EntityLeafPill } from '@/types/table';
import { formatEnumLabel } from '@/utils/fields';

/** The output arrays `pa_status_medbv` fans each leaf out across, in render order. */
const MEDBV_ENTITIES = ['drug', 'procedure'];

/** Section header the synthetic columns are grouped under in the Customize popover. */
const PA_STATUS_COLUMN_GROUP = 'Prior Authorization';

interface PaStatusLeaf {
  /** Leaf field id — also the search-dropdown key and the last segment of `fieldPath`. */
  leaf: string;
  /** Short display label, identical to the search dropdown's so the two agree. */
  label: string;
  /** Nested object inside a `drug[]`/`procedure[]` entry that holds this leaf. */
  subsection: string;
}

/**
 * The five leaves, in the order the search dropdown lists them.
 *
 * Deliberately NOT exported: knip hard-fails on unused value exports, and the only thing
 * outside this module that needs the list is the leaf-name set below.
 */
const PA_STATUS_COLUMN_LEAVES: readonly PaStatusLeaf[] = [
  {
    leaf: 'prior_authorization_on_file_status',
    label: 'PA Status',
    subsection: 'prior_auth_status',
  },
  { leaf: 'prior_authorization_required', label: 'PA Required', subsection: 'prior_auth_status' },
  {
    leaf: 'prior_authorization_on_file_exists',
    label: 'PA on File Exists',
    subsection: 'prior_auth_status',
  },
  { leaf: 'appeal_available', label: 'Appeal Available', subsection: 'prior_auth_denied' },
  { leaf: 'appeal_status', label: 'Appeal Status', subsection: 'prior_auth_denied' },
];

/**
 * Leaf names of the PA Status columns.
 *
 * `CasesContainer` uses this to suppress these leaves from the search dropdown's
 * column∩allow-list intersection: the workflow-gated block below it already offers them
 * unconditionally (so they are filterable without first adding a column), and without the
 * guard a selected column would list the same field twice.
 */
export const PA_STATUS_COLUMN_LEAF_IDS: ReadonlySet<string> = new Set(
  PA_STATUS_COLUMN_LEAVES.map(({ leaf }) => leaf),
);

/** The workflows that carry these leaves at all. */
const PA_STATUS_WORKFLOW_TYPES: ReadonlySet<string> = new Set<string>([
  WorkflowType.PA_STATUS_PBM,
  WorkflowType.PA_STATUS_MEDBV,
]);

/** Walk a parsed schema by field id, e.g. ['drug', 'prior_auth_status', 'appeal_status']. */
function findLeafField(groups: FieldGroup[], path: string[]): FormField | undefined {
  let candidates: FormField[] = groups.flatMap(group => group.fields ?? []);
  let found: FormField | undefined;

  for (const segment of path) {
    found = candidates.find(field => field.id === segment);
    if (!found) return undefined;
    candidates = found.fields ?? [];
  }

  return found;
}

/**
 * Resolve a leaf's enum value→label map off the parsed schema.
 *
 * Reads the already-parsed `FieldGroup[]` rather than `buildFieldEnumMap`, whose path
 * composition pushes ANY object carrying an `id` onto the key — so whether the key is
 * `drug.prior_auth_status.appeal_status` depends on details of the live schema we cannot
 * see from here, and a wrong key fails SILENTLY into `humanize` ("Appeal Status Approved").
 */
function resolveValueLabels(
  outputGroups: FieldGroup[],
  { leaf, subsection }: PaStatusLeaf,
): Record<string, string> {
  const field =
    findLeafField(outputGroups, [leaf]) ??
    MEDBV_ENTITIES.map(entity => findLeafField(outputGroups, [entity, subsection, leaf])).find(
      Boolean,
    );

  const labels: Record<string, string> = {};
  for (const option of field?.options ?? []) {
    labels[String(option.value)] = option.label;
  }
  return labels;
}

/**
 * Add the five PA Status columns to a workflow's output fields.
 *
 * Attach-or-append: a field already sitting at `output_data.data.<leaf>` (the flat
 * `pa_status_pbm` shape) keeps its id and group — so nobody's saved
 * `tableColumns_<workflowId>` selection is disturbed — and gains the descriptor plus the
 * short label, so the Customize dropdown reads the same on both workflows. Anything else
 * gets a synthetic column.
 *
 * Returns `outputFields` untouched for every other workflow type.
 */
export function applyPaStatusOutputFields(
  workflowType: string | undefined,
  outputGroups: FieldGroup[],
  outputFields: AvailableField[],
): AvailableField[] {
  if (!workflowType || !PA_STATUS_WORKFLOW_TYPES.has(workflowType)) return outputFields;

  const result = [...outputFields];

  for (const definition of PA_STATUS_COLUMN_LEAVES) {
    const fieldPath = `output_data.data.${definition.leaf}`;
    const existingIndex = result.findIndex(field => field.fieldPath === fieldPath);
    const flat = existingIndex !== -1;

    const cell: EntityLeafCell = {
      // A flat leaf needs no fan-out; `collectEntityLeafPills` reads `fieldPath` directly.
      entities: flat ? [] : [...MEDBV_ENTITIES],
      subsection: definition.subsection,
      leaf: definition.leaf,
      valueLabels: resolveValueLabels(outputGroups, definition),
    };

    if (flat) {
      result[existingIndex] = { ...result[existingIndex], label: definition.label, cell };
      continue;
    }

    result.push({
      // The `output_` prefix keeps `saveColumnConfig`'s and `createCasesTableConfig`'s
      // `type === 'output'` filters working unchanged.
      id: `output_pa_status_${definition.leaf}`,
      // Nominal — nothing lives here on medbv. Deliberately not the BARE leaf, which would
      // collide with the search dropdown's hardcoded key and give two <SelectItem>s the
      // same React key.
      fieldPath,
      label: definition.label,
      type: 'output',
      group: PA_STATUS_COLUMN_GROUP,
      cell,
    });
  }

  return result;
}

/** True for values that should not produce a pill at all. */
function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Render one leaf value the way the case-details panes do. */
function formatLeafValue(value: unknown, valueLabels: Record<string, string>): string {
  // The tri-state selects store lowercase 'yes'/'no'/'unknown' strings, which humanize
  // fine — but a raw JSON boolean would otherwise print "true".
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return formatEnumLabel(String(value), valueLabels);
}

/**
 * The element's pill code: its `code`, else its `name`, else its 1-based index.
 *
 * Mirrors `instanceLabel` in `utils/humanCall/fieldTree.ts` — not imported because that
 * helper is keyed on a `FormField` we do not have at cell-render time, and importing it
 * would drag `fieldStatus` in with it. Keep the two in step.
 */
function pillCode(element: Record<string, unknown>, index: number): string {
  const code = element.code;
  if (!isBlank(code) && typeof code !== 'object') return String(code);
  const name = element.name;
  if (!isBlank(name) && typeof name !== 'object') return String(name);
  return String(index + 1);
}

/**
 * Collect the pills for one cell.
 *
 * An entry whose subsection or leaf is missing is OMITTED rather than rendered as
 * `J1234 · --`: a code the rep has not reached yet is noise in a list view. An empty
 * result makes the cell render "N/A".
 */
export function collectEntityLeafPills(
  row: unknown,
  fieldPath: string,
  cell: EntityLeafCell,
): EntityLeafPill[] {
  // Flat shape (pa_status_pbm): a single value, so no code to prefix it with.
  if (cell.entities.length === 0) {
    const value: unknown = get(row, fieldPath);
    return isBlank(value) ? [] : [{ name: formatLeafValue(value, cell.valueLabels) }];
  }

  const pills: EntityLeafPill[] = [];

  for (const entity of cell.entities) {
    const elements: unknown = get(row, `output_data.data.${entity}`);
    if (!Array.isArray(elements)) continue;

    elements.forEach((element: unknown, index: number) => {
      if (typeof element !== 'object' || element === null || Array.isArray(element)) return;

      const record = element as Record<string, unknown>;
      const subsection = record[cell.subsection];
      if (typeof subsection !== 'object' || subsection === null) return;

      const value = (subsection as Record<string, unknown>)[cell.leaf];
      if (isBlank(value)) return;

      pills.push({
        code: pillCode(record, index),
        name: formatLeafValue(value, cell.valueLabels),
      });
    });
  }

  return pills;
}

/**
 * Plain-text form of a cell, for the copy button.
 *
 * Deliberately NOT truncated to `maxItems` the way the rendered pills are — a copied value
 * that says "+3 more" is useless.
 */
export function formatEntityLeafCopyValue(pills: EntityLeafPill[]): string {
  return pills.map(pill => (pill.code ? `${pill.code} · ${pill.name}` : pill.name)).join(', ');
}
